/**
 * #2005 INSTALLPAGE: which phone's home-screen steps the /install page shows.
 *
 * Pure functions over what the browser reports, so the page and its tests
 * read the same answer. Nothing here fetches or stores anything.
 * readInstallGuess and readStandalone are the two reads of the live browser.
 */

export type InstallPlatform = "ios" | "android";

/**
 * How well the browser the page is open in can add it:
 *  - "ok": Safari on iOS, Chrome on Android.
 *  - "other": another real browser. On iOS 16.4+ Chrome, Edge and Firefox can
 *    add from their own Share sheet, so the page advises Safari, it does not
 *    insist. On Android the steps name Chrome's menu, so it asks for Chrome.
 *  - "in-app": a web view inside another app (Mail, Outlook, Teams), which
 *    cannot add to the home screen at all.
 */
export type InstallBrowser = "ok" | "other" | "in-app";

export interface InstallGuess {
  /** The phone the steps are for, or null for a desktop or unknown device. */
  platform: InstallPlatform | null;
  browser: InstallBrowser;
}

export interface DeviceSignals {
  userAgent: string;
  /** navigator.maxTouchPoints; iPadOS reports a Mac user agent with touch. */
  maxTouchPoints?: number;
}

const IOS_DEVICE = /\b(iPhone|iPad|iPod)\b/;
// iOS browsers that are not Safari carry their own token; an in-app web view
// (Mail, Teams, Outlook) carries no "Safari/" token at all.
const IOS_NOT_SAFARI = /\b(CriOS|FxiOS|EdgiOS|OPiOS|GSA|YaBrowser|DuckDuckGo)\//;
const ANDROID_NOT_CHROME = /\b(SamsungBrowser|Firefox|EdgA|OPR|YaBrowser|DuckDuckGo)\//;
const ANDROID_WEBVIEW = /; wv\)/;

/** The phone this browser is on, read from navigator: the /install page and the #4974 install bar ask the same question. */
export function readInstallGuess(): InstallGuess {
  if (typeof navigator === "undefined") return { platform: null, browser: "ok" };
  return detectInstallPlatform({ userAgent: navigator.userAgent, maxTouchPoints: navigator.maxTouchPoints });
}

/**
 * True when the page is open as the installed app: the iPhone home-screen app
 * (navigator.standalone) or an installed Chrome or Edge window (the
 * display-mode media query). Shared by /install and the #4974 install bar and
 * menu item, which never offer an install inside the app itself.
 */
export function readStandalone(): boolean {
  if (typeof window === "undefined") return false;
  const nav = (typeof navigator === "undefined" ? {} : navigator) as Navigator & { standalone?: boolean };
  return nav.standalone === true || window.matchMedia?.("(display-mode: standalone)").matches === true;
}

export function detectInstallPlatform({ userAgent, maxTouchPoints = 0 }: DeviceSignals): InstallGuess {
  const ua = userAgent ?? "";
  if (/\bAndroid\b/i.test(ua)) {
    if (ANDROID_WEBVIEW.test(ua)) return { platform: "android", browser: "in-app" };
    const chrome = /\bChrome\//.test(ua) && !ANDROID_NOT_CHROME.test(ua);
    return { platform: "android", browser: chrome ? "ok" : "other" };
  }
  const ipadAsMac = /\bMacintosh\b/.test(ua) && maxTouchPoints > 1;
  if (IOS_DEVICE.test(ua) || ipadAsMac) {
    if (!/\bSafari\//.test(ua)) return { platform: "ios", browser: "in-app" };
    return { platform: "ios", browser: IOS_NOT_SAFARI.test(ua) ? "other" : "ok" };
  }
  return { platform: null, browser: "ok" };
}

/** The note for a browser that is not the one the steps name, or null. */
export function browserNote(platform: InstallPlatform, browser: InstallBrowser): string | null {
  if (browser === "ok") return null;
  if (platform === "ios" && browser === "other") return "Safari is the most reliable way to add it.";
  return `Open this page in ${BROWSER_NAME[platform]} to add it.`;
}

/**
 * `?for=iphone` / `?for=android` picks the steps outright, so a link on a
 * poster or in a message opens on the right phone's path.
 */
export function platformFromQuery(search: string): InstallPlatform | null {
  const value = new URLSearchParams(search).get("for")?.trim().toLowerCase();
  if (value === "iphone" || value === "ipad" || value === "ios") return "ios";
  if (value === "android") return "android";
  return null;
}

export interface InstallStep {
  /** What to tap, as the phone labels it. */
  title: string;
  /** One line of help, only where the control moves around. */
  hint?: string;
  icon: "safari" | "share" | "add-square" | "check" | "chrome" | "menu" | "install";
}

export const PLATFORM_LABEL: Record<InstallPlatform, string> = {
  ios: "iPhone or iPad",
  android: "Android",
};

export const BROWSER_NAME: Record<InstallPlatform, string> = {
  ios: "Safari",
  android: "Chrome",
};

export const INSTALL_STEPS: Record<InstallPlatform, InstallStep[]> = {
  ios: [
    { icon: "safari", title: "Open crosbe.ai in Safari" },
    { icon: "share", title: "Tap Share", hint: "Can't see it? Tap ⋯ first." },
    { icon: "add-square", title: "Tap Add to Home Screen", hint: "Scroll the list if you can't see it." },
    { icon: "check", title: "Tap Add" },
  ],
  android: [
    { icon: "chrome", title: "Open crosbe.ai in Chrome" },
    { icon: "menu", title: "Tap the ⋮ menu" },
    { icon: "install", title: "Tap Install app or Add to Home screen" },
    { icon: "check", title: "Tap Install to confirm" },
  ],
};

/** The sign-in-first warning the issue asks for, shared with the WHS copy. */
export const SIGN_IN_FIRST = "Sign in first, then add it.";
export const SIGN_IN_FIRST_REASON = "The home-screen app keeps the sign-in you have when you add it.";
